import { getItemOrDefaultToValue } from '../../details';
import COLORS from '../../constants/colors';
import { HIGH_DETAIL_CONFIG } from '../../constants/Map';
import { DEFAULT_EMPTY_VALUE } from 'utils/constants';
import {
  AUDIT_TRAIL_COLUMN_HEADERS,
  FOLLOWUP_REPORT_COLUMN_HEADERS,
} from 'utils/columns/incidentForm';
import { formatRoleNames } from 'utils/constants/roles';
import { displayTime } from 'utils/time';
import { v4 as uuidv4 } from 'uuid';

// for display purposes
export const getLocationValue = data => {
  const { locations } = data;
  const sitePolygon = [];
  if (data.details?.site?.boundary) {
    sitePolygon.push({ polygon: data.details.site.boundary });
  }
  return [...locations, ...sitePolygon].map(location => {
    if (location.point) {
      return {
        address: location.address,
        icon: data.general.isNewsItem ? 'news' : 'others',
        type: 'POINT',
        lat: location.point.latitude,
        lng: location.point.longitude,
      };
    } else if (location.polygon) {
      return {
        type: 'POLYGON',
        center: {
          lat: location.polygon.centreOfGravity.latitude,
          lng: location.polygon.centreOfGravity.longitude,
        },
        address: location?.address,
        boundary: location.polygon.points.map(point => ({
          lat: point.latitude,
          lng: point.longitude,
        })),
        tableView: false,
        riskLevel: data.general.isNewsItem
          ? data.general.category.toUpperCase()
          : 'OTHERS',
        strokeOpacity: HIGH_DETAIL_CONFIG.strokeOpacity,
        strokeWeight: HIGH_DETAIL_CONFIG.strokeWeight,
      };
    } else {
      return {
        ...location.ellipse,
        address: location.address,
        type: 'ELLIPSE',
        center: {
          lat: location.ellipse.centre.latitude,
          lng: location.ellipse.centre.longitude,
        },
        radiusX: location.ellipse.radiusX,
        radiusY: location.ellipse.radiusY,
        rotationAngle: location.ellipse.angle,
        color: data.general.isNewsItem ? COLORS.RED_01 : COLORS.BLUE_01,
        strokeOpacity: HIGH_DETAIL_CONFIG.strokeOpacity,
        strokeWeight: HIGH_DETAIL_CONFIG.strokeWeight,
        riskLevel: data.general.isNewsItem
          ? data.general.category.toUpperCase()
          : 'OTHERS',
      };
    }
  });
};

// location object for edit purposes
export const editableLocation = data => {
  const locationData =
    data.locations != null ? getItemOrDefaultToValue(data, 'locations') : [];
  return locationData.map((location, index) => {
    if (location.polygon) {
      return {
        isFilled: true,
        color: data.general.isNewsItem ? COLORS.RED_01 : COLORS.BLUE_01,
        paths: location.polygon.points.map(point => ({
          lat: point.latitude,
          lng: point.longitude,
        })),
        editable: true,
        draggable: false,
        strokeOpacity: HIGH_DETAIL_CONFIG.strokeOpacity,
        strokeWeight: HIGH_DETAIL_CONFIG.strokeWeight,
      };
    } else if (location.ellipse) {
      return {
        rotationAngle: location.ellipse.angle,
        radiusX: location.ellipse.radiusX,
        radiusY: location.ellipse.radiusY,
        center: {
          radius: location.ellipse.centre.radius,
          lat: location.ellipse.centre.latitude,
          lng: location.ellipse.centre.longitude,
          isWaypoint: location.ellipse.centre.isWaypoint,
        },
        color: data.general.isNewsItem ? COLORS.RED_01 : COLORS.BLUE_01,
        editable: true,
        draggable: true,
        strokeOpacity: HIGH_DETAIL_CONFIG.strokeOpacity,
        strokeWeight: HIGH_DETAIL_CONFIG.strokeWeight,
      };
    } else {
      return {
        lat: location.point.latitude,
        lng: location.point.longitude,
        seqNumber: index,
        icon: data.general.isNewsItem ? 'news' : 'others',
      };
    }
  });
};

export const auditTableTransform = data => {
  if (!data)
    return [
      {
        rowData: {
          [AUDIT_TRAIL_COLUMN_HEADERS.employeeId.field]: DEFAULT_EMPTY_VALUE,
          [AUDIT_TRAIL_COLUMN_HEADERS.name.field]: DEFAULT_EMPTY_VALUE,
          [AUDIT_TRAIL_COLUMN_HEADERS.role.field]: DEFAULT_EMPTY_VALUE,
          [AUDIT_TRAIL_COLUMN_HEADERS.action.field]: DEFAULT_EMPTY_VALUE,
          [AUDIT_TRAIL_COLUMN_HEADERS.date.field]: DEFAULT_EMPTY_VALUE,
        },
        allowEdit: false,
        allowDelete: false,
        rawData: {},
      },
    ];
  return data.map(item => {
    const { timestamp } = item;
    const { action } = item.detail;
    const { employeeId, name, roles } = item.detail.user;

    return {
      rowData: {
        [AUDIT_TRAIL_COLUMN_HEADERS.employeeId.field]:
          employeeId !== null ? employeeId : DEFAULT_EMPTY_VALUE,
        [AUDIT_TRAIL_COLUMN_HEADERS.name.field]:
          name !== '' ? name : DEFAULT_EMPTY_VALUE,
        [AUDIT_TRAIL_COLUMN_HEADERS.role.field]:
          roles !== null ? formatRoleNames(roles) : DEFAULT_EMPTY_VALUE,
        [AUDIT_TRAIL_COLUMN_HEADERS.action.field]: action
          ? action
          : DEFAULT_EMPTY_VALUE,
        [AUDIT_TRAIL_COLUMN_HEADERS.date.field]: displayTime({
          timestamp: timestamp,
        }),
      },
      allowEdit: false,
      allowDelete: false,
      rawData: item,
    };
  });
};

export const transformFollowUpEvent = data => {
  return data.map(item => {
    const { title } = item.general;
    const { eventId } = item.general;
    const { category } = item.general;
    const { type } = item.general;
    const { eventStartTime } = item.general;
    const { eventFinishTime } = item.general;
    return {
      rowData: {
        [FOLLOWUP_REPORT_COLUMN_HEADERS.title.field]: title,
        [FOLLOWUP_REPORT_COLUMN_HEADERS.eventId.field]: eventId,
        [FOLLOWUP_REPORT_COLUMN_HEADERS.eventCategory.field]: category,
        [FOLLOWUP_REPORT_COLUMN_HEADERS.eventType.field]: type,
        [FOLLOWUP_REPORT_COLUMN_HEADERS.eventStartTime.field]: displayTime({
          timestamp: eventStartTime,
        }),
        [FOLLOWUP_REPORT_COLUMN_HEADERS.eventFinishTime.field]: displayTime({
          timestamp: eventFinishTime,
        }),
      },
      allowEdit: false,
      allowDelete: true,
      rawData: item,
    };
  });
};

export const StartTimeCellRenderer = ({ data }) => {
  return displayTime({ timestamp: data.eventStartTime });
};
export const FinishTimeCellRenderer = ({ data }) => {
  return displayTime({ timestamp: data.eventFinishTime });
};

export const assignUniqueId = media => {
  return media.map(m => ({
    ...m,
    id: uuidv4(),
  }));
};
